import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import InventoryCard from "@/components/InventoryCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send, Store } from "lucide-react";

export default function Pendientes() {
  const [items, setItems] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalFilter, setSucursalFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [it, sc] = await Promise.all([
        api.get("/items", { params: { only_reorder: true } }),
        api.get("/sucursales"),
      ]);
      setItems(it.data);
      setSucursales(sc.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggle = async (item) => {
    try {
      const { data } = await api.post(`/items/${item.id}/toggle-reorder`);
      if (!data.needs_reorder) {
        setItems((prev) => prev.filter((it) => it.id !== item.id));
        toast.success(`"${data.name}" marcado como reordenado`);
      } else {
        setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error");
    }
  };

  const sendDigest = async () => {
    setSending(true);
    try {
      await api.post("/admin/send-digest");
      toast.success("Resumen enviado", {
        description: "Los Gerentes recibirán el correo en unos segundos.",
      });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  const sucursalesInItems = useMemo(() => {
    const ids = new Set(items.map((i) => i.sucursal_id).filter(Boolean));
    return sucursales.filter((s) => ids.has(s.id));
  }, [items, sucursales]);

  const filteredItems = useMemo(() => {
    if (sucursalFilter === "all") return items;
    return items.filter((it) => it.sucursal_id === sucursalFilter);
  }, [items, sucursalFilter]);

  const showSucursalFilter = sucursalesInItems.length > 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#4A3B32]" data-testid="pendientes-title">
            Pendientes de reorden
          </h1>
          <p className="text-[#8A796F] mt-1">
            {items.length === 0
              ? "Todo en orden por ahora."
              : `Toca una tarjeta cuando hayas reordenado el producto.`}
          </p>
        </div>
        <Button
          onClick={sendDigest}
          disabled={sending || items.length === 0}
          data-testid="send-digest-button"
          className="rounded-full bg-[#4A3B32] hover:bg-[#2F241D] text-white font-bold px-5 h-11 disabled:opacity-50"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Send className="w-4 h-4 mr-2" />
          )}
          Enviar resumen ahora
        </Button>
      </header>

      {showSucursalFilter && (
        <div className="flex items-center gap-2" data-testid="pendientes-sucursal-filter-wrap">
          <Store className="w-4 h-4 text-[#8A796F]" />
          <Select value={sucursalFilter} onValueChange={setSucursalFilter}>
            <SelectTrigger
              className="h-10 rounded-full border-[#EADBCE] bg-white w-auto min-w-[220px]"
              data-testid="pendientes-sucursal-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las sucursales</SelectItem>
              {sucursalesInItems.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {sucursalFilter !== "all" && (
            <button
              type="button"
              onClick={() => setSucursalFilter("all")}
              className="text-xs font-semibold text-[#8A796F] hover:text-[#FF5C5C] px-3 py-2 rounded-full"
            >
              Limpiar
            </button>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-white border border-[#EADBCE] rounded-3xl py-16 text-center" data-testid="pendientes-empty">
          <CheckCircle2 className="mx-auto w-14 h-14 text-[#A7E4D3]" />
          <h3 className="mt-4 font-display font-bold text-xl text-[#4A3B32]">¡Cero pendientes!</h3>
          <p className="text-sm text-[#8A796F] mt-1">Nada por reordenar en este momento.</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white border border-dashed border-[#EADBCE] rounded-3xl py-16 text-center">
          <p className="text-[#8A796F]">No hay pendientes en la sucursal seleccionada.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6" data-testid="pendientes-grid">
          {filteredItems.map((it) => (
            <InventoryCard key={it.id} item={it} onToggle={handleToggle} />
          ))}
        </div>
      )}
    </div>
  );
}
